import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useToggle from "../../Components/_utils/useToggle";
import beemg_arrow from '../../Assets/icons/beemg-arrow.svg';
import Loading from "../../Components/base/loading/loading";
import { useGetHelpDeskVideoURLQuery } from "../../Services/envApi";
import { useGetfaqsQuery, useUpdatefaqsMutation, useGetRelatedFaqsQuery } from "../../Services/faqsApi";
import { getCurrentUser } from "../../Services/userReducer";
import { useAppSelector } from "../../Store/hooks";
import Footer from "../Footer";
import Header from "../Header";
import SupportModal from "../SupportModel";
import SidebarModal from "./SideBarModal";
import Field from "../../Components/base/field/field";
import NewIcon from "../../Components/base/icon/newIcons";
import { Button } from "../../Components";

type FAQItem = {
  id: string;
  question: string;
  answer: string;
  faqId: string;
  faqOrder: number;
  tags: string[];
};

interface IBookmark {
  id: string;
  question: string;
  answer: string;
  faqId: string;
  faqOrder: number;
  tags: string[];
};

const VideoPlayer = ({
  videoUrl,
  videoKey,
  title,
}: {
  videoUrl: string;
  videoKey: string;
  title: string;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className="flex flex-col items-center w-full sm:w-1/2 md:w-1/3 lg:w-56 group">
      <div className="w-full aspect-video sm:aspect-video lg:h-40 rounded-md md:rounded-lg overflow-hidden bg-BeeMG-light-gray relative cursor-pointer">
        <video
          ref={videoRef}
          src={videoUrl}
          className={`w-full h-full object-cover ${isPlaying ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 hover:controls-visible`}
          controls
          poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
        <div
          className={`absolute inset-0 flex items-center justify-center ${isPlaying ? 'hidden' : 'flex'} transition-opacity duration-300`}
          onClick={handlePlay}
        >
          <div className="w-6 h-6 bg-BeeMG-light-gray bg-opacity-80 border-gray-400 rounded-full flex items-center justify-center border-2">
            <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>
      <p className="mt-2 text-center text-sm sm:text-base">{title}</p>
    </div>
  );
};

function HelpDesk() {
  const { t } = useTranslation();
  const location = useLocation();
  const currentUserDetail = useAppSelector(getCurrentUser);
  const { data: faqs, isLoading: faqsLoading } = useGetfaqsQuery();
  const { toggle: toggleSupportModel, isOpen: isSupportModelOpen } = useToggle();
  const [selectedFAQ, setSelectedFAQ] = useState<FAQItem | null>(null);
  const [bookmarks, setBookmarks] = useState<IBookmark[]>([]);
  const [showBookmarks, setShowBookmarks] = useState<boolean>(false);
  const [updateFaq] = useUpdatefaqsMutation();
  const [selectedVote, setSelectedVote] = useState<{ [key: string]: 'yes' | 'no' | null }>({});
  const [showRelatedFaqs, setShowRelatedFaqs] = useState<boolean>(false);
  const [relatedFaqId, setRelatedFaqId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { data: relatedFaqs } = useGetRelatedFaqsQuery(relatedFaqId ?? '', { skip: !relatedFaqId });

  // Highlight matching text
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark class="bg-BeeMG-yellow">$1</mark>');
  };

  // Filter and transform FAQs based on search
  const transformedData = useMemo(() => {
    const filteredFaqs = faqs?.filter(faq => {
      if (!searchQuery.trim()) return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        faq.question.toLowerCase().includes(searchLower) ||
        faq.answer.toLowerCase().includes(searchLower)
      );
    }) || [];

    return {
      rows: filteredFaqs.map((faq) => ({
        id: faq.id,
        question: searchQuery ? highlightText(faq.question, searchQuery) : faq.question,
        answer: faq.answer || 'No faqAnswer available',
        faqId: faq.faqId,
        faqOrder: faq.faqOrder,
        tags: faq.tags,
      })),
    };
  }, [faqs, searchQuery]);

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  useEffect(() => {
    const storedBookmarks = localStorage.getItem('bookmarks');
    if (storedBookmarks) setBookmarks(JSON.parse(storedBookmarks));
  }, []);

  const handleAddBookmark = () => {
    if (selectedFAQ) {
      const isAlreadyBookmarked = bookmarks.some((bm) => bm.id === selectedFAQ.id);
      if (!isAlreadyBookmarked) {
        const newBookmarks = [...bookmarks, selectedFAQ];
        setBookmarks(newBookmarks);
        localStorage.setItem('bookmarks', JSON.stringify(newBookmarks));
        alert('Bookmark added!');
      } else {
        alert('This FAQ is already bookmarked.');
      }
    }
  };

  const handleVote = async (id: string, type: 'yes' | 'no') => {
    try {
      await updateFaq({ id, type }).unwrap();
      setSelectedVote((prev) => ({ ...prev, [id]: type }));
      if (type === 'no') {
        toggleSupportModel();
      }
    } catch (error) {
      console.error('Error updating FAQ:', error);
    }
  };

  const handleToggleBookmarks = () => setShowBookmarks(!showBookmarks);

  const handleFAQClick = (faq: FAQItem) => {
    setSelectedFAQ(faq);
    const newUrl = `${location.pathname}?faq=${faq.id}`;
    window.history.pushState({}, '', newUrl);
  };

  const { data: HelpDeskVideoURL, isLoading: HelpDeskVideoURLLoading } = useGetHelpDeskVideoURLQuery();

  const videoEntries = HelpDeskVideoURL ? Object.entries(HelpDeskVideoURL) : [];
  const videofaqQuestionMapping: { [key: string]: string } = {
    beemg_intro1: 'BeeMG Intro',
    beemg_creator1: 'BeeMG Creator',
    beemg_collaborator1: 'BeeMG Collaborator',
    beemg_engagement1: 'BeeMG Engagement',
    beemg_monetize1: 'BeeMG Monetize',
  };

  const handleViewRelated = () => {
    if (!showRelatedFaqs && selectedFAQ) {
      setRelatedFaqId(selectedFAQ.id);
    }
    setShowRelatedFaqs(!showRelatedFaqs);
  };

  const RelatedContentModal = ({ showRelatedFaqs, handleViewRelated, relatedFaqs }: { showRelatedFaqs: boolean; handleViewRelated: () => void; relatedFaqs: FAQItem[] | undefined }) => {
    if (!showRelatedFaqs) return null;

    return (
      <SidebarModal
        title="Related Topics"
        items={relatedFaqs || []}
        isOpen={showRelatedFaqs}
        onClose={handleViewRelated}
        onItemClick={handleFAQClick}
        position="right"
      />
    );
  };

  if (HelpDeskVideoURLLoading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col">
      {!currentUserDetail && <Header />}
      <div className={`flex-grow ${currentUserDetail ? 'h-full' : ''}`}>
        <div className="w-full flex flex-col items-center justify-center">
          <div className="mb-16 gap-2 w-full flex place-items-center">
            <div className="w-full mt-6 px-0 mb-12">
              {/* Top row with bookmarks, title and search */}
              <div className="flex flex-col sm:flex-row items-center justify-between mb-8 mx-4 sm:mx-8 lg:mx-12">
                <div className="flex items-center gap-4 mb-4 sm:mb-0">
                  {!showBookmarks && (
                    <span
                      className="cursor-pointer text-sm sm:text-base ml-4 sm:ml-8"
                      onClick={handleToggleBookmarks}
                    >
                      {showBookmarks ? t('closeBookmarks') : t('bookmarkedTopics')}
                    </span>
                  )}
                </div>
                <div id="txt_faqs" data-testid="txt_faqs" className="text-lg sm:text-xl lg:text-2xl font-medium">
                  {t('BeeMG FAQs')}
                </div>
                <div className="w-full sm:max-w-xs lg:w-64">
                  <Field
                    name="policyName"
                    type="text"
                    data-testid="searchByName"
                    placeholder={t('search')}
                    icon={<NewIcon icon="SEARCH_GLASS" stroke="#535353" />}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                  />
                </div>
              </div>

              {showBookmarks && (
                <SidebarModal
                  title={t('bookmarkedTopics')}
                  items={bookmarks}
                  isOpen={showBookmarks}
                  onClose={handleToggleBookmarks}
                  onItemClick={handleFAQClick}
                  position="left"
                />
              )}

              {/* Scrollable Video Section */}
              <div className="max-h-[400px]">
                <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-4 mx-2 sm:mx-4">
                  {videoEntries.length > 0 ? (
                    videoEntries.map(([key, videoUrl], index) => (
                      <VideoPlayer
                        key={index}
                        videoUrl={videoUrl as string}
                        videoKey={key}
                        title={videofaqQuestionMapping[key + '1'] || `Video faqQuestion ${index + 1}`}
                      />
                    ))
                  ) : (
                    <p className="text-center">{t('noVideosAvailable')}</p>
                  )}
                </div>
              </div>

              {/* Horizontal divider */}
              <hr className="mt-2 border-t border-[3px] border-BeeMG-light-gray w-full sm:w-[80%] mx-auto" />

              {/* FAQ Section with vertical divider */}
              <div className="flex flex-col lg:flex-row lg:mx-36 mx-4 sm:mx-8 lg:gap-8 mt-0">
                {/* Left Column: FAQ List */}
                <div className="w-full lg:w-[40%] flex-shrink-0 pl-2 mt-6 lg:ml-20">
                  <h2 className="text-base sm:text-lg font-bold mb-4">
                    {t('whatAreYouLookingFor', { name: currentUserDetail ? currentUserDetail.firstName.charAt(0).toUpperCase() + currentUserDetail.firstName.slice(1) : '' })}
                  </h2>
                  <div className="pr-0 sm:pr-4 pb-4 min-h-[200px] lg:h-[250px] overflow-y-auto">
                    {transformedData.rows.map((item, index) => (
                      <div
                        key={index}
                        className={`cursor-pointer p-2 mb-2 rounded-md min-w-0 ${selectedFAQ?.question === item.question
                            ? 'bg-BeeMG-yellow'
                            : 'bg-BeeMG-light-gray'
                          }`}
                        onClick={() => handleFAQClick(item)}
                      >
                        <div className="flex items-center justify-between min-w-0">
                          <span
                            className="text-sm sm:text-sm truncate"
                            dangerouslySetInnerHTML={{ __html: item.question }}
                          ></span>
                          <img src={beemg_arrow} alt="Beemg Logo" width={12} height={12} className="flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Vertical Divider */}
                <div className="hidden lg:block h-[350px] w-[2px] bg-BeeMG-light-gray mx-4 flex-shrink-0"></div>

                {/* Right Column: FAQ Details */}
                <div className="w-full lg:w-[60%] pl-2 pr-0 sm:pr-4 mt-6 lg:mr-20 min-w-0 overflow-x-hidden">
                  {selectedFAQ ? (
                    <>
                      <h2 className="text-lg sm:text-xl font-semibold mb-4 break-words whitespace-normal">
                        {selectedFAQ.question}
                      </h2>
                      <div className="pr-0 sm:pr-4 pb-4 min-h-[150px] lg:h-[170px] overflow-y-auto overflow-x-hidden">
                        <div>
                          <p
                            className="mt-4 text-sm sm:text-base break-words whitespace-normal"
                            dangerouslySetInnerHTML={{
                              __html: highlightText(selectedFAQ.answer, searchQuery),
                            }}
                          ></p>
                        </div>
                      </div>
                      <div className="mt-4 text-sm sm:text-sm font-semibold">{t('isAnswerHelpful')}</div>
                      <div className="flex items-center justify-between mt-2 flex-wrap gap-2 sm:gap-4">
                        <div className="flex gap-2 items-center">
                          <Button
                            className={`px-2 py-1 text-BeeMG-dark-gray sm:text-sm ${selectedVote[selectedFAQ.id] === 'yes' ? 'bg-BeeMG-yellow' : ''}`}
                            onClick={() => handleVote(selectedFAQ.id, 'yes')}
                          >
                            {t('yes')}
                          </Button>
                          <Button
                            className={`px-2 py-1 text-BeeMG-dark-gray sm:text-sm ${selectedVote[selectedFAQ.id] === 'no' ? 'bg-BeeMG-yellow' : ''}`}
                            onClick={() => handleVote(selectedFAQ.id, 'no')}
                          >
                            {t('no')}
                          </Button>
                          <span
                            className="cursor-pointer text-sm sm:text-sm ml-2 sm:ml-8"
                            onClick={handleAddBookmark}
                          >
                            {bookmarks.some(bm => bm.id === selectedFAQ?.id) ? t('bookmarked') : t('addBookmark')}
                          </span>
                          <span
                            className="cursor-pointer text-BeeMG-dark-gray sm:text-sm ml-2"
                            onClick={handleViewRelated}
                          >
                            {showRelatedFaqs ? t('closeRelated') : t('viewRelated')}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-BeeMG-lightGray text-center text-sm sm:text-base">{t('selectTopicToView')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!currentUserDetail && <Footer />}
      {isSupportModelOpen && <SupportModal isSupportModelOpen={isSupportModelOpen} toggleSupportModel={toggleSupportModel} />}
      <RelatedContentModal
        showRelatedFaqs={showRelatedFaqs}
        handleViewRelated={handleViewRelated}
        relatedFaqs={relatedFaqs}
      />
    </div>
  );
}

export default HelpDesk;